import React, { useEffect, useState } from "react";
import { accountAuthError } from "../../api/accountAuthApi";
import { authButton, authInput } from "./PasswordFields";

export const normalizeWhatsAppPhone = (value) => {
  const compact = value.replace(/[\s()-]/g, "");
  if (!compact) return "";
  if (compact.startsWith("+")) return compact;
  if (/^91[0-9]{10}$/.test(compact)) return `+${compact}`;
  return `+91${compact}`;
};

export default function WhatsAppOtpForm({ request, verify, onVerified, enrollment = false, hasPassword = true }) {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [clock, setClock] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const normalizedPhone = normalizeWhatsAppPhone(phone);
  const international = phone.trim().startsWith("+") || /^91[0-9]{10}$/.test(phone.replace(/[\s()-]/g, ""));
  const phonePreview = normalizedPhone.startsWith("+91") ? `+91-${normalizedPhone.slice(3)}` : normalizedPhone;
  useEffect(() => {
    if (!challenge) return undefined;
    const timer = setInterval(() => setClock(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [challenge]);
  const resendSeconds = challenge ? Math.max(0, Math.ceil((challenge.resendAt - clock) / 1000)) : 0;
  const expiresSeconds = challenge ? Math.max(0, Math.ceil((challenge.expiresAt - clock) / 1000)) : 0;
  const send = async () => {
    if (!/^\+[1-9][0-9]{1,14}$/.test(normalizedPhone)
      || (normalizedPhone.startsWith("+91") && !/^\+91[0-9]{10}$/.test(normalizedPhone))
      || (!international && !/^[0-9]{10}$/.test(phone.replace(/[\s()-]/g, "")))) {
      setError("Enter a 10-digit Indian number, or include + and the country code for another country.");
      return;
    }
    setBusy(true); setError(""); setNotice("");
    try {
      const result = await request({ phone: normalizedPhone, ...(enrollment ? { current_password: password } : {}) });
      if (typeof result?.challenge_id !== "string" || !result.challenge_id
        || !Number.isFinite(result.expires_in) || result.expires_in <= 0
        || !Number.isFinite(result.resend_after) || result.resend_after < 0) {
        throw new Error("Invalid verification response. Please try again.");
      }
      const now = Date.now();
      setChallenge({ id: result.challenge_id, resendAt: now + result.resend_after * 1000, expiresAt: now + result.expires_in * 1000 });
      setClock(now); setOtp("");
      setNotice(enrollment ? "A verification code has been queued for this number."
        : "If this number is eligible, a login code will be sent to WhatsApp.");
    } catch (err) { setError(accountAuthError(err)); }
    finally { setBusy(false); }
  };
  const submit = async (event) => {
    event.preventDefault();
    if (!challenge) { await send(); return; }
    setBusy(true); setError("");
    try {
      const result = await verify({ challenge_id: challenge.id, otp });
      setPassword(""); setOtp("");
      await onVerified(result);
    } catch (err) { setError(accountAuthError(err)); }
    finally { setBusy(false); }
  };
  return <form aria-label={enrollment ? "Verify WhatsApp number" : "WhatsApp login"} className="space-y-4" onSubmit={submit}>
    {error && <p role="alert" className="text-rose-700">{error}</p>}
    {notice && <p role="status" className="text-sm text-slate-600">{notice}</p>}
    <label className="block text-sm font-semibold">WhatsApp number
      {phone && <span aria-hidden="true" className="ml-2 font-normal text-slate-600">{phonePreview}</span>}
      <div className="mt-1 flex items-center gap-2">
        {!international && <span aria-hidden="true" className="text-slate-600">+91</span>}
        <input aria-label="WhatsApp number" type="tel" autoComplete="tel" required placeholder="9876543210 or +441234567890"
          className={authInput} value={phone} disabled={Boolean(challenge)} onChange={(event) => setPhone(event.target.value)} />
      </div>
    </label>
    {enrollment && hasPassword && !challenge && <label className="block text-sm font-semibold">Current password for WhatsApp enrollment
      <input type="password" autoComplete="current-password" required className={authInput} value={password} onChange={(event) => setPassword(event.target.value)} />
    </label>}
    {challenge && <>
      <label className="block text-sm font-semibold">Verification code
        <input className={authInput} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6}
          required value={otp} onChange={(event) => setOtp(event.target.value)} />
      </label>
      <p className="text-xs text-slate-600">{expiresSeconds ? `Code expires in ${expiresSeconds}s` : "Code expired. Request another code."}</p>
    </>}
    <button className={authButton} disabled={busy || (Boolean(challenge) && !expiresSeconds)}>
      {busy ? "Please wait..." : challenge ? enrollment ? "Verify number" : "Verify and sign in" : "Send WhatsApp code"}
    </button>
    {challenge && <div className="flex gap-4 text-sm">
      <button type="button" disabled={busy || resendSeconds > 0} onClick={send} className="text-cyan-800 disabled:opacity-50">{resendSeconds ? `Resend in ${resendSeconds}s` : "Resend code"}</button>
      <button type="button" disabled={busy} onClick={() => { setChallenge(null); setOtp(""); setNotice(""); setError(""); }} className="text-cyan-800">Change number</button>
    </div>}
  </form>;
}
